import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Edit, Save, X, Plus, Trash2, RotateCcw,
  Crown, Star, Rocket, Award, Zap, Trophy, Medal, 
  Gem, Diamond, Sparkles, Flame, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Eye, Users, UserCheck, CheckCircle, Settings
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Icon mapping for dynamic rendering
const IconMap = {
  Crown, Star, Rocket, Award, Zap, Trophy, Medal,
  Gem, Diamond, Sparkles, Fire: Flame, Bolt: Zap, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Eye, Users, UserCheck, Verified: BadgeCheck, CheckCircle
};

// Render icon component dynamically
const renderIcon = (iconName, className = "w-5 h-5") => {
  const IconComponent = IconMap[iconName];
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  return <Star className={className} />;
};

// Icon selector component (moved outside main component)
const IconSelector = ({ value, onChange, availableIcons }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="relative">
      <div 
        className="flex items-center gap-2 border dark:border dark:border-gray-700-gray-600 rounded-lg p-2 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800 dark:bg-gray-700"
        onClick={() => setShowPicker(!showPicker)}
      >
        {renderIcon(value, "w-6 h-6")}
        <span className="text-sm text-gray-600 dark:text-gray-300">{value}</span>
      </div>
      
      {showPicker && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white dark:bg-gray-800 border dark:border dark:border-gray-700-gray-700 rounded-lg shadow-xl p-3 w-80 max-h-60 overflow-y-auto">
          <div className="grid grid-cols-6 gap-2">
            {availableIcons.map((icon) => (
              <button
                key={icon}
                type="button"
                onClick={() => {
                  onChange(icon);
                  setShowPicker(false);
                }}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  value === icon ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500' : ''
                }`}
                title={icon}
              >
                {renderIcon(icon, "w-5 h-5")}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Color selector component (moved outside main component)
const ColorSelector = ({ value, onChange, availableColors }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="relative">
      <div 
        className="flex items-center gap-2 border dark:border dark:border-gray-700-gray-600 rounded-lg p-2 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800 dark:bg-gray-700"
        onClick={() => setShowPicker(!showPicker)}
      >
        <div 
          className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-500 shadow"
          style={{ backgroundColor: value }}
        />
        <span className="text-sm text-gray-600 dark:text-gray-300">{value}</span>
      </div>
      
      {showPicker && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white dark:bg-gray-800 border dark:border dark:border-gray-700-gray-700 rounded-lg shadow-xl p-3 w-64">
          <div className="grid grid-cols-5 gap-2">
            {availableColors.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => {
                  onChange(color.value);
                  setShowPicker(false);
                }}
                className={`p-1 rounded-lg hover:scale-110 transition-transform ${
                  value === color.value ? 'ring-2 ring-blue-500' : ''
                }`}
                title={color.name}
              >
                <div 
                  className="w-8 h-8 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: color.value }}
                />
              </button>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t">
            <Label className="text-xs text-gray-500 dark:text-gray-400">Custom Color</Label>
            <Input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full h-8 p-0 border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const BoostCategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [createForm, setCreateForm] = useState({
    key: '',
    label: '',
    icon: 'Star',
    color: '#6B7280',
    description: '',
    sort_order: 0,
    is_default: false,
    // Badge display settings
    badge_text: '',
    badge_size: 'medium',
    font_size: 'xs',
    icon_size: 'small',
    position: 'top-left',
    show_icon: true,
    show_text: true
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/boost-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Error",
        description: "Failed to fetch boost categories",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  const fetchOptions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/boost-categories/options`);
      if (response.ok) {
        const data = await response.json();
        setAvailableIcons(data.icons || []);
        setAvailableColors(data.colors || []);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchCategories();
    fetchOptions();
  }, [fetchCategories, fetchOptions]);

  const handleCreate = async () => {
    if (!createForm.key || !createForm.label) {
      toast({
        title: "Error",
        description: "Key and Label are required",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/boost-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...createForm,
          sort_order: parseInt(createForm.sort_order) || categories.length + 1
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Boost category created successfully",
        });
        setShowCreateModal(false);
        setCreateForm({
          key: '',
          label: '',
          icon: 'Star',
          color: '#6B7280',
          description: '',
          sort_order: 0,
          is_default: false,
          badge_text: '',
          badge_size: 'medium',
          font_size: 'xs',
          icon_size: 'small',
          position: 'top-left',
          show_icon: true,
          show_text: true
        });
        fetchCategories();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to create category",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Error",
        description: "Failed to create category",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (category) => {
    setEditing(category.category_id);
    setEditForm({
      label: category.label,
      icon: category.icon,
      color: category.color,
      description: category.description || '',
      sort_order: category.sort_order,
      is_default: category.is_default,
      // Badge display settings
      badge_text: category.badge_text || category.label,
      badge_size: category.badge_size || 'medium',
      font_size: category.font_size || 'xs',
      icon_size: category.icon_size || 'small',
      position: category.position || 'top-left',
      show_icon: category.show_icon !== false,
      show_text: category.show_text !== false
    });
  };

  const handleSave = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/boost-categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Category updated successfully",
        });
        setEditing(null);
        fetchCategories();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to update category",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/boost-categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Category deleted successfully",
        });
        fetchCategories();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to delete category",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleRestore = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/boost-categories/${categoryId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Category restored successfully",
        });
        fetchCategories();
      }
    } catch (error) {
      console.error('Error restoring category:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading categories...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            Boost Categories
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage boost package categories, icons, and colors
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-6 border-b dark:border-gray-700 shrink-0">
              <h3 className="text-xl font-bold dark:text-white">Create New Category</h3>
              <Button onClick={() => setShowCreateModal(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
              <div>
                <Label className="dark:text-gray-200">Key (unique identifier) *</Label>
                <Input
                  value={createForm.key}
                  onChange={(e) => setCreateForm({ ...createForm, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="e.g., premium, elite, gold"
                  className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Lowercase, no spaces (used internally)</p>
              </div>

              <div>
                <Label className="dark:text-gray-200">Display Label *</Label>
                <Input
                  value={createForm.label}
                  onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                  placeholder="e.g., Premium Package"
                  className="mt-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Icon</Label>
                  <div className="mt-1">
                    <IconSelector
                      value={createForm.icon}
                      onChange={(icon) => setCreateForm({ ...createForm, icon })}
                      availableIcons={availableIcons}
                    />
                  </div>
                </div>

                <div>
                  <Label>Color</Label>
                  <div className="mt-1">
                    <ColorSelector
                      value={createForm.color}
                      onChange={(color) => setCreateForm({ ...createForm, color })}
                      availableColors={availableColors}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Brief description of this category"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={createForm.sort_order}
                    onChange={(e) => setCreateForm({ ...createForm, sort_order: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>

                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={createForm.is_default}
                    onChange={(e) => setCreateForm({ ...createForm, is_default: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_default">Default category</Label>
                </div>
              </div>

              {/* Badge Display Settings */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Badge Display Settings
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <Label>Badge Text (shown on card)</Label>
                    <Input
                      value={createForm.badge_text}
                      onChange={(e) => setCreateForm({ ...createForm, badge_text: e.target.value })}
                      placeholder="e.g., PREMIUM, BOOSTED, VIP"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leave empty to use Display Label</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Badge Size</Label>
                      <select
                        value={createForm.badge_size}
                        onChange={(e) => setCreateForm({ ...createForm, badge_size: e.target.value })}
                        className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                    <div>
                      <Label>Font Size</Label>
                      <select
                        value={createForm.font_size}
                        onChange={(e) => setCreateForm({ ...createForm, font_size: e.target.value })}
                        className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="xs">Extra Small</option>
                        <option value="sm">Small</option>
                        <option value="base">Normal</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Icon Size</Label>
                      <select
                        value={createForm.icon_size}
                        onChange={(e) => setCreateForm({ ...createForm, icon_size: e.target.value })}
                        className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                    <div>
                      <Label>Position</Label>
                      <select
                        value={createForm.position}
                        onChange={(e) => setCreateForm({ ...createForm, position: e.target.value })}
                        className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="top-left">Top Left</option>
                        <option value="top-right">Top Right</option>
                        <option value="bottom-left">Bottom Left</option>
                        <option value="bottom-right">Bottom Right</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="show_icon_create"
                        checked={createForm.show_icon}
                        onChange={(e) => setCreateForm({ ...createForm, show_icon: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="show_icon_create">Show Icon</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="show_text_create"
                        checked={createForm.show_text}
                        onChange={(e) => setCreateForm({ ...createForm, show_text: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="show_text_create">Show Text</Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <Label className="text-xs text-gray-500 dark:text-gray-400 mb-2 block">Preview</Label>
                <div className="flex items-center gap-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${createForm.color}20` }}
                  >
                    {renderIcon(createForm.icon, "w-6 h-6")}
                  </div>
                  <div>
                    <Badge 
                      className={`flex items-center gap-1 ${
                        createForm.font_size === 'xs' ? 'text-xs' : 
                        createForm.font_size === 'sm' ? 'text-sm' : 'text-base'
                      } ${
                        createForm.badge_size === 'small' ? 'px-1.5 py-0.5' : 
                        createForm.badge_size === 'large' ? 'px-3 py-1.5' : 'px-2 py-1'
                      }`}
                      style={{ 
                        backgroundColor: createForm.color,
                        color: 'white'
                      }}
                    >
                      {createForm.show_icon && renderIcon(createForm.icon, 
                        createForm.icon_size === 'small' ? 'w-3 h-3' : 
                        createForm.icon_size === 'large' ? 'w-5 h-5' : 'w-4 h-4'
                      )}
                      {createForm.show_text && (
                        <span>{(createForm.badge_text || createForm.label || 'CATEGORY').toUpperCase()}</span>
                      )}
                    </Badge>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{createForm.description || 'Description'}</p>
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Fixed Footer with Buttons */}
            <div className="flex justify-end gap-3 p-6 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl shrink-0">
              <Button onClick={() => setShowCreateModal(false)} variant="outline" className="px-6">
                Cancel
              </Button>
              <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700 px-6">
                Create Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <Card 
            key={category.category_id} 
            className={`relative transition-all hover:shadow-lg ${!category.active ? 'opacity-60 bg-gray-50 dark:bg-gray-700' : ''}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {editing === category.category_id ? (
                    <IconSelector
                      value={editForm.icon}
                      onChange={(icon) => setEditForm({ ...editForm, icon })}
                      availableIcons={availableIcons}
                    />
                  ) : (
                    <div 
                      className="p-3 rounded-xl shadow-sm"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      {renderIcon(category.icon, "w-7 h-7")}
                    </div>
                  )}
                  <div className="flex-1">
                    {editing === category.category_id ? (
                      <Input
                        value={editForm.label}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                        className="font-semibold dark:text-white"
                      />
                    ) : (
                      <CardTitle className="text-lg">{category.label}</CardTitle>
                    )}
                    <code className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded mt-1 inline-block">
                      {category.key}
                    </code>
                  </div>
                </div>
                
                <div className="flex gap-1">
                  {editing === category.category_id ? (
                    <>
                      <Button onClick={() => handleSave(category.category_id)} size="sm" variant="ghost" className="text-green-600">
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => setEditing(null)} size="sm" variant="ghost">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => handleEdit(category)} size="sm" variant="ghost">
                        <Edit className="w-4 h-4" />
                      </Button>
                      {category.active ? (
                        <Button 
                          onClick={() => handleDelete(category.category_id)} 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleRestore(category.category_id)} 
                          size="sm" 
                          variant="ghost" 
                          className="text-green-600 hover:text-green-700"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="space-y-3">
                {/* Color */}
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Color</Label>
                  {editing === category.category_id ? (
                    <ColorSelector
                      value={editForm.color}
                      onChange={(color) => setEditForm({ ...editForm, color })}
                      availableColors={availableColors}
                    />
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <div 
                        className="w-6 h-6 rounded-full border-2 border-white shadow"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-sm font-mono">{category.color}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Description</Label>
                  {editing === category.category_id ? (
                    <Input
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      placeholder="Description"
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      {category.description || '-'}
                    </p>
                  )}
                </div>

                {/* Badge Display Settings - Edit Mode */}
                {editing === category.category_id && (
                  <div className="border-t pt-3 mt-3 space-y-3">
                    <h4 className="font-semibold text-xs flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <Settings className="w-3 h-3" />
                      Badge Display Settings
                    </h4>
                    
                    <div>
                      <Label className="text-xs text-gray-500 dark:text-gray-400">Badge Text</Label>
                      <Input
                        value={editForm.badge_text}
                        onChange={(e) => setEditForm({ ...editForm, badge_text: e.target.value })}
                        placeholder="e.g., PREMIUM"
                        className="mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500 dark:text-gray-400">Badge Size</Label>
                        <select
                          value={editForm.badge_size}
                          onChange={(e) => setEditForm({ ...editForm, badge_size: e.target.value })}
                          className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 dark:text-gray-400">Font Size</Label>
                        <select
                          value={editForm.font_size}
                          onChange={(e) => setEditForm({ ...editForm, font_size: e.target.value })}
                          className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="xs">Extra Small</option>
                          <option value="sm">Small</option>
                          <option value="base">Normal</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500 dark:text-gray-400">Icon Size</Label>
                        <select
                          value={editForm.icon_size}
                          onChange={(e) => setEditForm({ ...editForm, icon_size: e.target.value })}
                          className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 dark:text-gray-400">Position</Label>
                        <select
                          value={editForm.position}
                          onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                          className="w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="top-left">Top Left</option>
                          <option value="top-right">Top Right</option>
                          <option value="bottom-left">Bottom Left</option>
                          <option value="bottom-right">Bottom Right</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          id={`show_icon_${category.category_id}`}
                          checked={editForm.show_icon}
                          onChange={(e) => setEditForm({ ...editForm, show_icon: e.target.checked })}
                          className="w-3 h-3"
                        />
                        <Label htmlFor={`show_icon_${category.category_id}`} className="text-xs">Show Icon</Label>
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          id={`show_text_${category.category_id}`}
                          checked={editForm.show_text}
                          onChange={(e) => setEditForm({ ...editForm, show_text: e.target.checked })}
                          className="w-3 h-3"
                        />
                        <Label htmlFor={`show_text_${category.category_id}`} className="text-xs">Show Text</Label>
                      </div>
                    </div>

                    {/* Edit Preview */}
                    <div className="bg-gray-100 dark:bg-gray-700 rounded p-2">
                      <Label className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 block">Preview</Label>
                      <Badge 
                        className={`flex items-center gap-1 ${
                          editForm.font_size === 'xs' ? 'text-xs' : 
                          editForm.font_size === 'sm' ? 'text-sm' : 'text-base'
                        } ${
                          editForm.badge_size === 'small' ? 'px-1.5 py-0.5' : 
                          editForm.badge_size === 'large' ? 'px-3 py-1.5' : 'px-2 py-1'
                        }`}
                        style={{ 
                          backgroundColor: editForm.color,
                          color: 'white'
                        }}
                      >
                        {editForm.show_icon && renderIcon(editForm.icon, 
                          editForm.icon_size === 'small' ? 'w-3 h-3' : 
                          editForm.icon_size === 'large' ? 'w-5 h-5' : 'w-4 h-4'
                        )}
                        {editForm.show_text && (
                          <span>{(editForm.badge_text || editForm.label || 'BADGE').toUpperCase()}</span>
                        )}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Badge Preview - View Mode */}
                {editing !== category.category_id && (
                  <div className="border-t pt-2 mt-2">
                    <Label className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 block">Badge Preview</Label>
                    <Badge 
                      className={`flex items-center gap-1 ${
                        category.font_size === 'xs' ? 'text-xs' : 
                        category.font_size === 'sm' ? 'text-sm' : 'text-base'
                      } ${
                        category.badge_size === 'small' ? 'px-1.5 py-0.5' : 
                        category.badge_size === 'large' ? 'px-3 py-1.5' : 'px-2 py-1'
                      }`}
                      style={{ 
                        backgroundColor: category.color,
                        color: 'white'
                      }}
                    >
                      {category.show_icon !== false && renderIcon(category.icon, 
                        category.icon_size === 'small' ? 'w-3 h-3' : 
                        category.icon_size === 'large' ? 'w-5 h-5' : 'w-4 h-4'
                      )}
                      {category.show_text !== false && (
                        <span>{(category.badge_text || category.label || 'BADGE').toUpperCase()}</span>
                      )}
                    </Badge>
                  </div>
                )}

                {/* Meta info */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  {category.is_default && (
                    <Badge variant="outline" className="text-xs">
                      Default
                    </Badge>
                  )}
                  <Badge 
                    variant={category.active ? "default" : "secondary"}
                    className="text-xs"
                    style={category.active ? { backgroundColor: category.color } : {}}
                  >
                    {category.active ? 'Active' : 'Inactive'}
                  </Badge>
                  <span className="text-xs text-gray-400 ml-auto">
                    Order: {category.sort_order}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {categories.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">No Categories Found</h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Create your first boost category to get started.</p>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Category
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BoostCategoriesManager;
